import { MatRadioChange } from '@angular/material/radio';
import { Component, ElementRef, Input, OnChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { Handshake, Parity, PROTOCOL_FORM_PARAMS, StopBits } from 'src/app/models/protocol/protocol.model';
import { disableFields, enableFields, setProtocolFormValues } from '../../utils/protocolFormUtils';
import { CommunicationMethodTypeCode } from '../../../models/protocol/protocol.model';
import { SiteDeviceFccDTO } from 'src/app/models/Sites/siteFccDTO';

const DEFAULT_FCC_EVENT_CLIENT_IP: string = 'AUTO';
const DEFAULT_FCC_EVENT_CLIENT_PORT: number = 2001;

@Component({
  selector: 'protocol-form',
  templateUrl: './protocol-form.component.html',
  styleUrls: ['./protocol-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProtocolFormComponent implements OnChanges {
  @Input() entity?: SiteDeviceFccDTO;
  @Input() set parentFormGroup(formGroup: FormGroup) {
    this._parentFormGroup = formGroup;
    this.createProtocolFormGroup();
  }
  @Input() set disableAll(disableProtocol: boolean) {
    this.protocolDisabled = disableProtocol;
  };
  @Input() set defaultPort(port: number) {
    const portControl = this._parentFormGroup.get('protocol')?.get(PROTOCOL_FORM_PARAMS.port);

    if (portControl) {
      if (!portControl.dirty) {
        portControl.setValue(port);
      }
    }
    else {
      this.pendingPort = port;
    }

  }
  @Input() enableFccEventFields: boolean = false;

  get protocolFormGroup(): FormGroup {
    return this._parentFormGroup.get('protocol') as FormGroup;
  }

  get recipientAddressTableArray(): FormArray {
    return this.protocolFormGroup.get('recipientAddressTable') as FormArray;
  }

  get recipientAddressControls(): FormGroup[] {
    return this.recipientAddressTableArray.controls as FormGroup[];
  }

  communicationTypes = Object.values(CommunicationMethodTypeCode);
  parityTypes = Object.values(Parity)
  stopBitsTypes = Object.values(StopBits);
  handshakeTypes = Object.values(Handshake)
  _parentFormGroup: FormGroup;
  
  isRadioEnabled: boolean = true;

  protocolDisabled: boolean;

  isFocused = false;

  private pendingPort?: number;

  @ViewChild('recipientAddButton', { read: ElementRef })
  private recipientAddButton?: ElementRef<HTMLButtonElement>;

  constructor(
    private fb: FormBuilder
  ) { }

  ngOnChanges() {
    if (this.protocolDisabled) {
      this.disableProtocolFields();
    } else {
      this.enableProtocolFields();
    }
  }

  createProtocolFormGroup() {
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode, this.fb.control('IP_PROTOCOL'));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.ipAddress, this.fb.control(null, Validators.required));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.port, this.fb.control(this.pendingPort ?? null, [Validators.required, Validators.min(1), Validators.max(65535), Validators.pattern('^[0-9]+$')]));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.address, this.fb.control({ value: null, disabled: true }));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.baudRate, this.fb.control({ value: null, disabled: true }));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.deviceId, this.fb.control({value: null, disabled: true}));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.localNodeAddress,
      this.createLonNodesAddressFormGroup()
    );
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.recipientAddressTable,
      this.fb.array([])
    );
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.maxBlockLength, this.fb.control({value: null, disabled: true}));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.heartBeatInterval, this.fb.control({value:null, disabled: true}))
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.parity, this.fb.control({ value: null, disabled: true }));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.dataBits, this.fb.control({ value: null, disabled: true }));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.stopBits, this.fb.control({ value: null, disabled: true }));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.handshake, this.fb.control({ value: null, disabled: true }));
    this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.timeout, this.fb.control({ value: null, disabled: true }));

    if (this.enableFccEventFields) {
      this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.forecourtEventClientIp, this.fb.control(DEFAULT_FCC_EVENT_CLIENT_IP));
      this.protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.forecourtEventClientPort, this.fb.control(DEFAULT_FCC_EVENT_CLIENT_PORT, [Validators.required, Validators.min(1), Validators.max(65535), Validators.pattern('^[0-9]+$')]));
    }

    this.onProtocolTypeChanges(this.protocolFormGroup);
    this.onForecourtEventClientIpChanges(this.protocolFormGroup);
    this.onForecourtEventClientPortChanges(this.protocolFormGroup);
    setProtocolFormValues(this.protocolFormGroup, this.entity);
    this.setRecipientAddressFormArray(this.entity?.communicationMethodData?.recipientAddressTable);
  }

  addRecipientAddress(){
    const recipients = this.recipientAddressTableArray;
    recipients.push(this.createLonNodesAddressFormGroup());
    this.protocolFormGroup.markAsDirty();

    setTimeout(() => {
      this.recipientAddButton?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    });
  }

  removeRecipientAddress(index: number){
    const recipients = this.recipientAddressTableArray;
    recipients.removeAt(index)
    this.protocolFormGroup.markAsDirty();
  }

  setRecipientAddressFormArray(recipientAddressTable: any[] = []){
    const recipients = this.recipientAddressTableArray;
    recipients.clear();

    recipientAddressTable.forEach(({
      logicalDeviceId, subnet, node
    }) => {
      recipients.push(
        this.createLonNodesAddressFormGroup(logicalDeviceId, subnet, node)
      )
    })
  }

  createLonNodesAddressFormGroup(
    logicalDeviceId?: number,
    subnet?: number,
    node?: number
  ): FormGroup {
    return this.fb.group({
      [PROTOCOL_FORM_PARAMS.logicalDeviceId]: [logicalDeviceId ?? null, [Validators.required]],
      [PROTOCOL_FORM_PARAMS.subnet]: [subnet ?? null, [Validators.required]],
      [PROTOCOL_FORM_PARAMS.node]: [node ?? null, [Validators.required]]
    })
  }

  protocolCommChange(event: MatRadioChange){
    this._parentFormGroup
      .get('protocol')
      .get(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode)
      .setValue(event.value);
    this._parentFormGroup.markAsDirty();
  }

  onProtocolTypeChanges(protocolForm: FormGroup) {
    protocolForm.get(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode).valueChanges.subscribe((type) => {
      if (type === 'SERIAL_CONNECTION') {
        disableFields(protocolForm, [
          PROTOCOL_FORM_PARAMS.ipAddress, 
          PROTOCOL_FORM_PARAMS.port,
          PROTOCOL_FORM_PARAMS.deviceId,
          PROTOCOL_FORM_PARAMS.heartBeatInterval,
          PROTOCOL_FORM_PARAMS.localNodeAddress,
          PROTOCOL_FORM_PARAMS.maxBlockLength,
          PROTOCOL_FORM_PARAMS.recipientAddressTable
        ], false);
        enableFields(protocolForm, [
          PROTOCOL_FORM_PARAMS.address, 
          PROTOCOL_FORM_PARAMS.baudRate,
          PROTOCOL_FORM_PARAMS.stopBits,
          PROTOCOL_FORM_PARAMS.parity,
          PROTOCOL_FORM_PARAMS.dataBits,
          PROTOCOL_FORM_PARAMS.handshake,
          PROTOCOL_FORM_PARAMS.timeout
        ]);
      } else if (type === 'LON_CONNECTION'){
        enableFields(protocolForm, [
          PROTOCOL_FORM_PARAMS.address, 
          PROTOCOL_FORM_PARAMS.deviceId, 
          PROTOCOL_FORM_PARAMS.port,
          PROTOCOL_FORM_PARAMS.maxBlockLength,
          PROTOCOL_FORM_PARAMS.heartBeatInterval,
          PROTOCOL_FORM_PARAMS.localNodeAddress,
          PROTOCOL_FORM_PARAMS.logicalDeviceId,
          PROTOCOL_FORM_PARAMS.subnet,
          PROTOCOL_FORM_PARAMS.node
        ]);
        disableFields(protocolForm, [
          PROTOCOL_FORM_PARAMS.ipAddress,
          PROTOCOL_FORM_PARAMS.baudRate,
          PROTOCOL_FORM_PARAMS.stopBits,
          PROTOCOL_FORM_PARAMS.dataBits,
          PROTOCOL_FORM_PARAMS.handshake,
          PROTOCOL_FORM_PARAMS.timeout,
          PROTOCOL_FORM_PARAMS.parity
        ], false)
      }
      else {
        disableFields(protocolForm, [
          PROTOCOL_FORM_PARAMS.address, 
          PROTOCOL_FORM_PARAMS.baudRate,
          PROTOCOL_FORM_PARAMS.address, 
          PROTOCOL_FORM_PARAMS.deviceId,
          PROTOCOL_FORM_PARAMS.maxBlockLength,
          PROTOCOL_FORM_PARAMS.heartBeatInterval,
          PROTOCOL_FORM_PARAMS.localNodeAddress,
          PROTOCOL_FORM_PARAMS.logicalDeviceId,
          PROTOCOL_FORM_PARAMS.subnet,
          PROTOCOL_FORM_PARAMS.node,
          PROTOCOL_FORM_PARAMS.stopBits,
          PROTOCOL_FORM_PARAMS.parity,
          PROTOCOL_FORM_PARAMS.dataBits,
          PROTOCOL_FORM_PARAMS.handshake,
          PROTOCOL_FORM_PARAMS.timeout
        ], false);
        enableFields(protocolForm, [PROTOCOL_FORM_PARAMS.ipAddress, PROTOCOL_FORM_PARAMS.port]);
      }
    });
  }

  onForecourtEventClientIpChanges(protocolForm: FormGroup) {
    const forecourtEventClientIpControl = protocolForm.get(PROTOCOL_FORM_PARAMS.forecourtEventClientIp);
    if (forecourtEventClientIpControl) {
      forecourtEventClientIpControl.valueChanges
        .pipe(debounceTime(1000))
        .subscribe((ip) => {
          if (ip === null || ip === "") {
            forecourtEventClientIpControl.setValue(DEFAULT_FCC_EVENT_CLIENT_IP, { emitEvent: false });
          }
        });
    }
  }
  
  onForecourtEventClientPortChanges(protocolForm: FormGroup) {
    const forecourtEventClientPortControl = protocolForm.get(PROTOCOL_FORM_PARAMS.forecourtEventClientPort);
    if (forecourtEventClientPortControl) {
      forecourtEventClientPortControl.valueChanges
        .pipe(debounceTime(1000))
        .subscribe((port) => {
          if (port === null || port === "") {
            forecourtEventClientPortControl.setValue(DEFAULT_FCC_EVENT_CLIENT_PORT, { emitEvent: false });
          }
        });
    }
  }

  disableProtocolFields() {
    const protocolFormGroup = this._parentFormGroup.get('protocol') as FormGroup;

    disableFields(
      protocolFormGroup, 
      [
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode,
        PROTOCOL_FORM_PARAMS.ipAddress,
        PROTOCOL_FORM_PARAMS.port,
        PROTOCOL_FORM_PARAMS.address,
        PROTOCOL_FORM_PARAMS.baudRate,
        PROTOCOL_FORM_PARAMS.parity,
        PROTOCOL_FORM_PARAMS.dataBits,
        PROTOCOL_FORM_PARAMS.handshake,
        PROTOCOL_FORM_PARAMS.dataBits,
        PROTOCOL_FORM_PARAMS.timeout,
        PROTOCOL_FORM_PARAMS.localNodeAddress,
        PROTOCOL_FORM_PARAMS.recipientAddressTable,
        PROTOCOL_FORM_PARAMS.subnet,
        PROTOCOL_FORM_PARAMS.logicalDeviceId,
        PROTOCOL_FORM_PARAMS.maxBlockLength,
        PROTOCOL_FORM_PARAMS.heartBeatInterval,
        PROTOCOL_FORM_PARAMS.node
      ], false
    );
  }

  enableProtocolFields() {
    const protocolFormGroup = this._parentFormGroup.get('protocol') as FormGroup;
    this.createProtocolFormGroup();
    enableFields(protocolFormGroup, [PROTOCOL_FORM_PARAMS.communicationMethodTypeCode]);
  }
}
